import React, { FormEventHandler, FunctionComponent } from 'react';
import { useEventCallback } from '../../hooks/useEventCallback';
import { Icon, IconSize, IconType } from '../../misc/Icon/Icon';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { classes } from '../../utils/utils';
import { InputLayout, InputLayoutProps } from '../InputLayout/InputLayout';
import sharedStyles from '../sharedStyles.module.css';
import styles from './SearchInput.module.css';

export type SearchInputProps = PropsWithoutChildren<
  Omit<
    InputLayoutProps,
    | 'children'
    | 'onInput'
    | 'onChange'
    | 'onInputCapture'
    | 'onChangeCapture'
    | 'label'
  >
> & {
  value: string;
  onInput: (value: string) => void;
  placeholder?: string;
};

export const SearchInput: FunctionComponent<SearchInputProps> = React.memo(
  (props) => {
    const { value, onInput, placeholder, ...remainingProps } = prepareProps({
      props,
      ignoreChildren: true
    });

    const handleInput: FormEventHandler<HTMLInputElement> = useEventCallback(
      (event) => {
        onInput(event.currentTarget.value);
      }
    );

    const handleClearButtonClick = useEventCallback(() => {
      onInput('');
    });

    return (
      <InputLayout
        label=""
        {...remainingProps}
      >
        {() => (
          <div className={styles.contentWrapper}>
            <div className={styles.inputWrapper}>
              <input
                type="text"
                className={classes(sharedStyles.inputBaseStyle, styles.input)}
                value={value}
                onInput={handleInput}
                placeholder={placeholder}
                data-testid="SearchInput.Input"
              ></input>
              {value ? (
                <button
                  type="button"
                  className={styles.clearButton}
                  onClick={handleClearButtonClick}
                  data-testid="SearchInput.ClearButton"
                >
                  <Icon
                    name="times"
                    type={IconType.Regular}
                    size={IconSize.Small}
                  ></Icon>
                </button>
              ) : null}
            </div>
            <div className={styles.searchIconWrapper}>
              <Icon
                name="magnifying-glass"
                type={IconType.Regular}
                size={IconSize.Small}
              ></Icon>
            </div>
          </div>
        )}
      </InputLayout>
    );
  }
);
